import { Component, Suspense } from "react";
import { Button } from "@/components/ui/button";

const RELOAD_ONCE_KEY = "ttt_chunk_reload_once";
const RELOAD_GUARD_REARM_MS = 10000;

/**
 * A lazy chunk missing on disk (post-deploy, old tab) throws either a native
 * ChunkLoadError (webpack) or a "failed/error loading dynamically imported
 * module" TypeError, depending on browser. Anything else is a real render
 * bug in the loaded chunk, not a staleness problem — must not trigger a
 * reload loop for those.
 */
function isChunkLoadError(error) {
  const message = String(error?.message || error || "");
  return (
    error?.name === "ChunkLoadError" ||
    /loading chunk .* failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

function hasReloadedOnce() {
  try {
    return sessionStorage.getItem(RELOAD_ONCE_KEY) === "1";
  } catch {
    return false;
  }
}

function markReloadedOnce() {
  try {
    sessionStorage.setItem(RELOAD_ONCE_KEY, "1");
  } catch {
    /* ignore */
  }
}

function clearReloadedOnce() {
  try {
    sessionStorage.removeItem(RELOAD_ONCE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Wraps a lazy route's Suspense boundary so a failed chunk never leaves the
 * root blank. A stale bundle after a deploy throws ChunkLoadError when the
 * browser fetches a hashed chunk that no longer exists on disk — one
 * automatic reload picks up the new index.html/manifest. If the chunk is
 * still missing after that (a real failure, not staleness), this stops
 * reloading and shows a manual-recovery screen instead of looping.
 */
export class ChunkLoadErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.rearmTimer = null;
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidMount() {
    // Suspense children can reject after this boundary mounts. Keep a spent
    // guard long enough to catch that delayed failure; otherwise a missing
    // chunk clears the guard before it throws and reloads forever.
    if (!this.state.error) {
      this.rearmTimer = window.setTimeout(() => {
        if (!this.state.error) clearReloadedOnce();
        this.rearmTimer = null;
      }, RELOAD_GUARD_REARM_MS);
    }
  }

  componentWillUnmount() {
    if (this.rearmTimer !== null) window.clearTimeout(this.rearmTimer);
  }

  componentDidCatch(error, info) {
    if (this.rearmTimer !== null) {
      window.clearTimeout(this.rearmTimer);
      this.rearmTimer = null;
    }
    console.error("[ChunkLoadErrorBoundary]", error, info);
    if (isChunkLoadError(error) && !hasReloadedOnce()) {
      markReloadedOnce();
      window.location.reload();
    }
  }

  render() {
    const { error } = this.state;
    if (error) {
      const chunkError = isChunkLoadError(error);
      if (chunkError && !hasReloadedOnce()) {
        // The automatic reload fires from componentDidCatch, which runs
        // after this render — show a spinner for that sub-second window
        // rather than nothing.
        return (
          <div className="flex items-center justify-center py-20">
            <div className="spinner" />
          </div>
        );
      }
      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center px-6 py-12 text-center">
          <h2 className="text-xl font-bold text-[#2D2D2D]">This page failed to load</h2>
          <p className="mt-2 max-w-md text-sm text-[#2D2D2D]/70">
            {chunkError
              ? "A new version was published while this tab was open. Reload to pick it up."
              : "Something broke loading this page. Try reloading, or go back to the home screen."}
          </p>
          <Button type="button" className="mt-4 btn-primary" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Suspense + ChunkLoadErrorBoundary, bundled for every lazy-route call site. */
export function LazyBoundary({ fallback, children }) {
  return (
    <ChunkLoadErrorBoundary>
      <Suspense fallback={fallback}>{children}</Suspense>
    </ChunkLoadErrorBoundary>
  );
}
